import {
  CheckCircleOutline,
  Close,
  ErrorOutline,
  WarningAmber,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface ExecutionResult {
  executionId: string;
  memberName?: string;
  memberEmail?: string;
  error?: string;
}

interface ExecutionResultDialogProps {
  open: boolean;
  onClose: () => void;
  successes: ExecutionResult[];
  failures: ExecutionResult[];
  totalCount: number;
  actionType?: "execute" | "approve" | "delete";
}

export default ({
  open,
  onClose,
  successes,
  failures,
  totalCount,
  actionType = "execute",
}: ExecutionResultDialogProps) => {
  const [currentTab, setCurrentTab] = useState(failures.length > 0 ? 1 : 0);

  const successCount = successes.length;
  const failureCount = failures.length;

  const getTitle = () => {
    if (actionType === "execute") {
      return "Execution Results";
    } else if (actionType === "approve") {
      return "Approval Results";
    } else {
      return "Deletion Results";
    }
  };

  const getActionText = () => {
    if (actionType === "execute") {
      return "executed";
    } else if (actionType === "approve") {
      return "approved";
    } else {
      return "deleted";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{getTitle()}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            {successCount > 0 && failureCount === 0 && (
              <Box display="flex" alignItems="center" sx={{ color: "success.main" }}>
                <CheckCircleOutline sx={{ mr: 1 }} />
                Successfully {getActionText()} all {totalCount} action{totalCount !== 1 ? "s" : ""}
              </Box>
            )}
            {successCount > 0 && failureCount > 0 && (
              <Box display="flex" alignItems="center" sx={{ color: "warning.main" }}>
                <WarningAmber sx={{ mr: 1 }} />
                {successCount} of {totalCount} action{totalCount !== 1 ? "s" : ""} {getActionText()} successfully
              </Box>
            )}
            {successCount === 0 && failureCount > 0 && (
              <Box display="flex" alignItems="center" sx={{ color: "error.main" }}>
                <ErrorOutline sx={{ mr: 1 }} />
                Failed to {actionType} all {totalCount} action{totalCount !== 1 ? "s" : ""}
              </Box>
            )}
          </Typography>
        </Box>

        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label={`Successful (${successCount})`}
            disabled={successCount === 0}
          />
          <Tab
            label={`Failed (${failureCount})`}
            disabled={failureCount === 0}
          />
        </Tabs>

        {/* Success List */}
        {currentTab === 0 && successCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <List dense>
              {successes.map((success, index) => (
                <ListItem key={success.executionId || index}>
                  <ListItemIcon>
                    <CheckCircleOutline color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={success.memberName || "Unknown"}
                    secondary={success.memberEmail}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Failure List */}
        {currentTab === 1 && failureCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <List dense>
              {failures.map((failure, index) => (
                <ListItem key={failure.executionId || index}>
                  <ListItemIcon>
                    <ErrorOutline color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={failure.memberName || "Unknown"}
                    secondary={
                      <>
                        {failure.memberEmail && (
                          <Typography variant="caption" display="block">
                            {failure.memberEmail}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                        >
                          {failure.error || "Unknown error"}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
